import { U21Chip, U21Spacer } from 'app/shared/u21-ui/components';
import { SavedFiltersMenuButton } from 'app/modules/filters/components/SavedFiltersMenuButton';
import { isEqual } from 'lodash';
import { AppliedSavedFilterChip } from 'app/modules/filters/components/AppliedSavedFilterChip';
import { SaveFilterButton } from 'app/modules/filters/components/SaveFilterButton';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Filter,
  FilterContext,
  FilterOption,
  SavedFilter,
} from 'app/modules/filters/models';
import { useSelector } from 'react-redux';
import { selectAgent } from 'app/modules/session/selectors';
import { useGetSavedFilters } from 'app/modules/filters/queries/useGetSavedFilters';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import styled from 'styled-components';
import { getValidFilters } from 'app/modules/filters/utils';
import { IconPinFilled } from '@u21/tabler-icons';

interface Props {
  appliedSavedFilter: SavedFilter | undefined;
  defaultFilters: Filter[];
  filters: Filter[];
  filterContext: FilterContext;
  onChange: (filters: Filter[]) => void;
  options: FilterOption[];
  pinnedSavedFiltersStorageKey: LocalStorageKeys;
  setAppliedSavedFilter: Dispatch<SetStateAction<SavedFilter | undefined>>;
}

export const SavedFilters = ({
  appliedSavedFilter,
  defaultFilters,
  filters,
  filterContext,
  onChange,
  options,
  pinnedSavedFiltersStorageKey,
  setAppliedSavedFilter,
}: Props) => {
  const [pinnedSavedFilterIds, setPinnedSavedFilterIds] = useLocalStorageState<
    number[]
  >(pinnedSavedFiltersStorageKey, []);

  // pending applied filter held until parent's filters catch up — avoids
  // flashing dirty state in the gap between dispatch and parent re-render
  const pendingApplySavedFilterRef = useRef<SavedFilter>();

  const agent = useSelector(selectAgent);

  const { data: savedFilters = [], isSuccess: savedFiltersLoaded } =
    useGetSavedFilters(filterContext);

  const optionMap = useMemo(
    () =>
      options.reduce<Record<FilterOption['key'], FilterOption>>(
        (acc, i) => ({ ...acc, [i.key]: i }),
        {},
      ),
    [options],
  );

  const resolveSavedFilters = useCallback(
    (saved: Filter[]): Filter[] => {
      const valid = getValidFilters(saved, options);
      const validKeys = new Set(valid.map((i) => i.key));
      const missingRequired = defaultFilters.filter(
        (i) => optionMap[i.key]?.required && !validKeys.has(i.key),
      );
      return [...missingRequired, ...valid];
    },
    [defaultFilters, options, optionMap],
  );

  const appliedSavedFilters = useMemo(
    () =>
      savedFilters.filter((i) =>
        isEqual(resolveSavedFilters(i.filters), filters),
      ),
    [savedFilters, filters, resolveSavedFilters],
  );

  const appliedSavedFilterIds = useMemo(
    () => new Set(appliedSavedFilters.map((i) => i.id)),
    [appliedSavedFilters],
  );

  // when no applied state set + current filters match a saved filter, lock onto the first match
  const appliedSavedFilterRef = useRef(appliedSavedFilter);
  useEffect(() => {
    appliedSavedFilterRef.current = appliedSavedFilter;
  }, [appliedSavedFilter]);
  useEffect(() => {
    if (!appliedSavedFilterRef.current && appliedSavedFilters.length > 0) {
      setAppliedSavedFilter(appliedSavedFilters[0]);
    }
  }, [appliedSavedFilters, setAppliedSavedFilter]);

  // when viewing a non-owned saved filter, clear applied saved filter when
  // current filters do not match
  useEffect(() => {
    const applied = appliedSavedFilterRef.current;
    if (
      applied &&
      applied.created_by_id !== agent.id &&
      !isEqual(resolveSavedFilters(applied.filters), filters)
    ) {
      setAppliedSavedFilter(undefined);
    }
  }, [filters, agent.id, resolveSavedFilters, setAppliedSavedFilter]);

  const staleSavedFilterIds = useMemo(
    () =>
      new Set(
        savedFilters
          .filter(
            (i) =>
              getValidFilters(i.filters, options).length < i.filters.length,
          )
          .map((i) => i.id),
      ),
    [options, savedFilters],
  );

  const onApplySavedFilter = useCallback(
    (savedFilter: SavedFilter) => {
      onChange(resolveSavedFilters(savedFilter.filters));
      pendingApplySavedFilterRef.current = savedFilter;
    },
    [onChange, resolveSavedFilters],
  );

  // commit applied state once parent's filters reflect what we set —
  // avoids dirty flash between dispatch and parent re-render
  useEffect(() => {
    const pending = pendingApplySavedFilterRef.current;
    if (!pending) {
      return;
    }
    if (isEqual(resolveSavedFilters(pending.filters), filters)) {
      pendingApplySavedFilterRef.current = undefined;
      setAppliedSavedFilter(pending);
    }
  }, [filters, resolveSavedFilters, setAppliedSavedFilter]);

  const savedFilterChips = useMemo(() => {
    const idToFilter = new Map(savedFilters.map((i) => [i.id, i]));
    const ids = new Set(pinnedSavedFilterIds);
    if (appliedSavedFilter) {
      ids.add(appliedSavedFilter.id);
    }
    return Array.from(ids)
      .map((id) => idToFilter.get(id))
      .filter((i): i is SavedFilter => i !== undefined);
  }, [appliedSavedFilter, pinnedSavedFilterIds, savedFilters]);

  // prune pinned ids that no longer exist (deleted, no longer shared, etc.)
  useEffect(() => {
    if (!savedFiltersLoaded) {
      return;
    }
    const validIds = new Set(savedFilters.map((i) => i.id));
    const pruned = pinnedSavedFilterIds.filter((id) => validIds.has(id));
    if (pruned.length !== pinnedSavedFilterIds.length) {
      setPinnedSavedFilterIds(pruned);
    }
  }, [
    pinnedSavedFilterIds,
    savedFilters,
    savedFiltersLoaded,
    setPinnedSavedFilterIds,
  ]);

  return (
    <U21Spacer wrap>
      <SavedFiltersMenuButton
        appliedSavedFilter={appliedSavedFilter}
        appliedSavedFilterIds={appliedSavedFilterIds}
        currentFilters={filters}
        filterContext={filterContext}
        onApply={onApplySavedFilter}
        onAppliedSavedFilterChange={setAppliedSavedFilter}
        onPinnedSavedFilterIdsChange={setPinnedSavedFilterIds}
        pinnedSavedFilterIds={pinnedSavedFilterIds}
        savedFilters={savedFilters}
        staleSavedFilterIds={staleSavedFilterIds}
      />
      {savedFilterChips.map((i) => {
        const isApplied = appliedSavedFilter?.id === i.id;
        const pinned = pinnedSavedFilterIds.includes(i.id);
        if (isApplied) {
          return (
            <AppliedSavedFilterChip
              key={i.id}
              defaultFilters={defaultFilters}
              dirty={!isEqual(resolveSavedFilters(i.filters), filters)}
              filters={filters}
              filterContext={filterContext}
              onAppliedSavedFilterChange={setAppliedSavedFilter}
              onFiltersChange={onChange}
              onPinnedSavedFilterIdsChange={setPinnedSavedFilterIds}
              pinned={pinned}
              savedFilter={i}
            />
          );
        }
        return (
          <StyledU21Chip
            key={i.id}
            icon={pinned ? <StyledIconPinFilled /> : undefined}
            onClick={() => {
              onApplySavedFilter(i);
            }}
            rounded
            variant="outlined"
          >
            {i.title}
          </StyledU21Chip>
        );
      })}
      <RightActions>
        <SaveFilterButton
          appliedSavedFilter={appliedSavedFilter}
          defaultFilters={defaultFilters}
          duplicateSavedFilter={appliedSavedFilters[0]}
          filterContext={filterContext}
          filters={filters}
          onAppliedSavedFilterChange={setAppliedSavedFilter}
        />
      </RightActions>
    </U21Spacer>
  );
};

const RightActions = styled.div`
  margin-left: auto;
`;

const StyledU21Chip = styled(U21Chip)`
  color: ${(props) => props.theme.palette.text.secondary};

  .MuiChip-icon {
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

const StyledIconPinFilled = styled(IconPinFilled)`
  color: ${(props) => props.theme.palette.text.secondary};
`;
